/**
 * useJobDetail Hook
 *
 * React Query hook for fetching a single job's details.
 */

import { isInFlightWorkOrderStatus } from '@/domains/jobs/constants/inFlightWorkOrderStatus';
import { queryKeys } from '@/lib/query-client';
import { PERMIT_CATEGORY, PERMIT_STATUS } from '@skewb-works/contracts';
import type { AddJobTaskBody, JobDetail } from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addJobTask, getJobDetail, removeJobTask, setJobTaskMandatory } from '../api/jobDetailApi';

const JOB_DETAIL_POLL_INTERVAL_MS = 5000;

// Poll job detail while a permit state is still resolving, so the card and
// status gates update as soon as CORE's next event lands (WRK-5704).
// exported for unit test — pure predicate, no React dependency (CWL-145)
export function shouldPollForPermit(data: JobDetail | undefined): boolean {
  if (!data) return false;

  const category = data.permit_category ?? null;
  if (category === PERMIT_CATEGORY.NONE) return false;

  // Awaiting the category, or awaiting the permit row once the category resolved.
  if (category == null || category === PERMIT_CATEGORY.PENDING) return true;
  const permit = data.permit;
  if (!permit) return true;

  // Permit exists — poll only while a lifecycle action is in flight.
  // CWL-145: PENDING_EXTENSION belongs here too. It was omitted, so after an
  // extension request the card never refreshed to show CORE's answer.
  return (
    permit.status === PERMIT_STATUS.PENDING_START ||
    permit.status === PERMIT_STATUS.PENDING_EXTENSION ||
    permit.status === PERMIT_STATUS.PENDING_STOP
  );
}

// CWL-386: also poll while the job's own lifecycle is mid-flight (En Route /
// On Site), independent of permit state — a job with no permit workflow must
// still refresh the badge without a manual reload.
export function shouldPollForLifecycle(
  data: { work_order_status: string | null } | undefined,
): boolean {
  return isInFlightWorkOrderStatus(data?.work_order_status);
}

export function useJobDetail(jobId: number) {
  const query = useQuery({
    queryKey: queryKeys.jobs.detail(String(jobId)),
    queryFn: () => getJobDetail(jobId),
    enabled: jobId > 0,
    refetchInterval: (q) =>
      shouldPollForPermit(q.state.data) || shouldPollForLifecycle(q.state.data)
        ? JOB_DETAIL_POLL_INTERVAL_MS
        : false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useAddJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddJobTaskBody) => addJobTask(jobId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Added',
      successMessage: 'Job task added successfully',
    },
  });
}

/**
 * Override a task's mandatory state for this job only. Pass `null` to clear the
 * override and fall back to the job category default.
 */
export function useSetJobTaskMandatory(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, isMandatory }: { taskId: number; isMandatory: boolean | null }) =>
      setJobTaskMandatory(jobId, taskId, isMandatory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Updated',
      successMessage: 'Task requirement updated for this job',
    },
  });
}

export function useRemoveJobTask(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) => removeJobTask(jobId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(String(jobId)) });
    },
    meta: {
      successTitle: 'Task Removed',
      successMessage: 'Job task removed successfully',
    },
  });
}
